// homeTabStart.js — THE HOME TABS' OWN SCREENS (Nika 2026-10-23: "instead of
// [fly,dashboard,agent] rename [Main, Dashboard, Chat, Present, Build] and
// draw related experience for each tab aligning AI mode").
//
// A tab whose mode makes nothing until it is asked shows ITS start screen
// (components/HomeTabStart.js) in the Fly start screen's shape — the day and
// the workspace, one heading in two inks, one line on what the tab makes, ask
// tiles, and what the person already has of that kind. Everything is made from
// this workspace (its apps, its starters, its saved decks — nothing per
// tenant, nothing hardcoded):
//   chat     the conversation's empty state (components/AssistantMainThread.js):
//            the workspace's own starters (functions/assistantStarters.js)
//   present  Presentation mode before a deck: a deck of the week, of what needs
//            the person, of each of its apps; the decks already made
// A tile asks in the tab's mode through the composer's own path (homeAiSend:
// with the main placement the answer becomes the home's screen — a deck, the
// conversation).
//
// Build has no start screen of its own: its tab IS the app builder, the
// /add-app page on the home (functions/homeBuild.js; Nika 2026-10-23: "build
// tab should reuse ui/ux of /add-apps") — the builder's own empty state, its
// ready-made apps and the person's apps are where a build starts.
//
// FRANK: every export is self-contained; siblings through `this.call`.

// Where a start screen shows. Present: its mode on the home with the tabs on
// screen (functions/homeView.js homeTabsOnScreen), nothing made yet — a deck
// on screen or the conversation holding the home takes the area instead.
// Chat: the conversation itself, empty (wherever the thread shows — its view
// gates the rest). Build: never (its tab is the app builder,
// functions/homeBuild.js). Every key is read up front (reactive callers).
export const homeTabStartShown = function homeTabStartShown(root, tab) {
  const r = root && typeof root === 'object' ? root : {}
  const fns = (this.context && this.context.functions) || {}
  const mode = r.assistantMode
  const list = Array.isArray(r.chatMessages) ? r.chatMessages : []
  const thread =
    typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  const deck =
    typeof fns.assistantDeckShown === 'function' ? !!this.call('assistantDeckShown', r) : false
  const onScreen =
    typeof fns.homeTabsOnScreen === 'function' ? !!this.call('homeTabsOnScreen', r) : false
  if (tab === 'chat') return thread && list.length === 0
  if (!onScreen || thread) return false
  if (tab === 'present') return mode === 'presentation' && !deck
  return false
}

// The heading in two inks (whom you ask, then what you get) and the one line
// under it — every key written out (the i18n gate reads literals).
export const homeTabStartCopy = function homeTabStartCopy(root, tab) {
  const KEYS = {
    // Chat's line is the conversation's own (its empty hint under the tiles,
    // components/AssistantMainThread.js ThreadEmpty): no second one here.
    chat: {
      lead: 'workspace.homeTab.chat.titleLead',
      rest: 'workspace.homeTab.chat.titleRest',
      line: '',
      tiles: 'workspace.flyStart.tilesLabel'
    },
    present: {
      lead: 'workspace.homeTab.present.titleLead',
      rest: 'workspace.homeTab.present.titleRest',
      line: 'workspace.homeTab.present.lead',
      tiles: 'workspace.flyStart.tilesLabel',
      kept: 'workspace.homeTab.present.kept'
    }
  }
  const keys = KEYS[tab] || KEYS.chat
  const word = (key) => {
    const v = this.call('polyglot', key)
    return v == null ? '' : String(v)
  }
  const scope = String(this.call('assistantScopeName', root) || '')
  return {
    lead: word(keys.lead).replace('{workspace}', scope),
    rest: word(keys.rest),
    line: keys.line ? word(keys.line) : '',
    tiles: word(keys.tiles),
    kept: keys.kept ? word(keys.kept) : ''
  }
}

// The screen is the HOME's (pages/main.js's grid area, or the home's
// conversation) — not a view the page layer lays over another route
// (components/AssistantMainLayer.js, `mainHost: 'over'`): its cards wear the
// home's glass there, the solid card over a page. Read from the tree.
export const homeTabStartOnHome = function homeTabStartOnHome(el) {
  let node = el || this
  for (let i = 0; node && i < 24; i++) {
    if (node.mainHost === 'over') return false
    node = node.parent
  }
  return true
}

// The card a press wears there: the home's glass (its workspace re-tint
// channel), else the solid card.
export const homeTabStartCard = function homeTabStartCard(el) {
  return this.call('homeTabStartOnHome', el)
    ? 'var(--home-card-bg, var(--theme-card-background))'
    : 'cardSolid'
}

// THE ASKS — the Fly start tile's row shape (components/AssistantFlyStart.js
// AssistantFlyStartTile), six at most, each a sentence in the tab's own mode.
//   chat     the workspace's starters (the sidebar's and Fly's own set)
//   present  the week · what needs attention · one deck per app
// The workspace's OWN apps come first — its business: a CRM, a production
// board (the launcher's own group for them, `custom`) — then the house apps
// people work in. The platform's builder views (the launcher's `views` group:
// Apps, Infrastructure, Agents) are no subject for a deck (seen live on dev
// 2026-10-23: "Present Apps: where we stand" before Production).
export const homeTabStartTiles = function homeTabStartTiles(root, tab) {
  const r = root && typeof root === 'object' ? root : {}
  const MAX = 6
  const word = (key) => {
    const v = this.call('polyglot', key)
    return v == null ? '' : String(v)
  }
  const safe = (v) => String(v == null ? '' : v).replace(/[^A-Za-z0-9]+/g, '')
  const out = []
  const seen = new Set()
  const push = (key, text, icon, app, extra) => {
    const t = String(text || '').trim()
    if (!t || seen.has(t.toLowerCase()) || out.length >= MAX) return
    seen.add(t.toLowerCase())
    out.push({
      key: 'homeTab' + safe(tab) + safe(key || t),
      kind: 'ask',
      hero: false,
      text: t,
      prompt: t,
      count: '',
      words: '',
      severity: '',
      signal: '',
      app: String(app || ''),
      icon: icon || 'magicstar',
      more: '',
      localAction: '',
      form: '',
      span: 1,
      tall: false,
      ...(extra || {})
    })
  }

  if (tab === 'chat') {
    for (const row of this.call('workspaceStarters', r) || []) {
      push(
        row && row.key,
        row && row.text,
        row && row.icon,
        row && row.caption,
        typeof (row && row.localAction) === 'string' && row.localAction
          ? {
              localAction: row.localAction,
              prompt: String(row.prompt || row.text || ''),
              // a starter that opens an app's own form (assistantStarterForm)
              ...(typeof row.form === 'string' && row.form ? { form: row.form } : {})
            }
          : { prompt: String((row && (row.prompt || row.text)) || '') }
      )
    }
    return out
  }
  if (tab !== 'present') return []

  // The apps the launcher shows (the flag and access law already applied):
  // the workspace's own first, the builder views left out.
  const builder = new Set()
  for (const group of this.call('searchAppGroupsFor', r) || []) {
    if (!group || group.key !== 'views') continue
    for (const app of group.apps || []) if (app && app.route) builder.add(app.route)
  }
  const tiles = (this.call('visibleStarterTiles', r) || []).filter(
    (t) => t && t.route && !builder.has(t.route)
  )
  const ranked = [...tiles.filter((t) => t.custom), ...tiles.filter((t) => !t.custom)]
  // An installed app's tile (it carries `appKey`) by its App Store name,
  // through installedAppName (Nika 2026-10-23: "app names = what a person sees
  // in the workspace App Store") — "Present Insights: where we stand", never
  // the manifest's "Analytics". A house tile's key and a customer's own words
  // read through polyglot, as before.
  const nameOf = (t) =>
    String(
      (t.appKey && this.call('installedAppName', { appKey: t.appKey, title: t.title })) || ''
    ) ||
    word(t.title) ||
    String(t.title || '')
  const scope = String(this.call('assistantScopeName', r) || '')

  push('week', word('workspace.homeTab.present.week').replace('{workspace}', scope), 'date', scope)
  push('attention', word('workspace.homeTab.present.attention'), 'lnAttention', scope)
  for (const t of ranked) {
    const name = nameOf(t)
    if (name)
      push(t.route, word('workspace.homeTab.present.app').replace('{app}', name), t.icon, name)
  }
  return out
}

// What the person already has of the tab's kind: Present's decks (the deck
// made on the home and not saved yet, then the saved boards of kind 'deck'). A
// press opens it (homeTabStartOpen).
export const homeTabStartItems = function homeTabStartItems(root, tab) {
  const r = root && typeof root === 'object' ? root : {}
  const MAX = 6
  const safe = (v) => String(v == null ? '' : v).replace(/[^A-Za-z0-9]+/g, '')
  const word = (key) => {
    const v = this.call('polyglot', key)
    return v == null ? '' : String(v)
  }
  if (tab === 'present') {
    const out = []
    const deck = r._aiDeck
    if (
      deck &&
      deck.id &&
      !deck.savedId &&
      deck.status === 'ready' &&
      !deck.preview &&
      (deck.title || deck.prompt)
    ) {
      out.push({
        key: 'homeTabDeck' + safe(deck.id),
        kind: 'deck',
        id: String(deck.id),
        title: String(deck.title || deck.prompt),
        meta: word('workspace.homeTab.present.unsaved')
      })
    }
    for (const b of Array.isArray(r._aiBoards) ? r._aiBoards : []) {
      if (out.length >= MAX) break
      const isDeck = b && b.id && (b.kind === 'deck' || (b.spec && b.spec.kind === 'deck'))
      if (!isDeck) continue
      const at = Date.parse(b.updatedAt || b.updated_at || b.createdAt || b.created_at || '')
      out.push({
        key: 'homeTabDeck' + safe(b.id),
        kind: 'deck',
        id: String(b.id),
        title: String(b.title || (b.spec && b.spec.title) || b.id),
        at: Number.isFinite(at) ? at : 0,
        meta: ''
      })
    }
    return out
  }
  return []
}

// A tile's press: a starter's local action, else the ask, in the tab's mode,
// through the composer's own send (functions/homeAiSend.js).
export const homeTabStartAsk = function homeTabStartAsk(s) {
  const tile = s && typeof s.parse === 'function' ? s.parse() : s
  if (!tile || typeof tile !== 'object' || tile.kind === 'skeleton') return null
  const fns = (this.context && this.context.functions) || {}
  if (tile.localAction && typeof fns[tile.localAction] === 'function') {
    // the tile rides along: a local action that needs its row (a starter
    // naming an app's form, assistantStarterForm) reads it; others ignore it
    return this.call(tile.localAction, tile)
  }
  const prompt = String(tile.prompt || tile.text || '').trim()
  if (!prompt || typeof fns.homeAiSend !== 'function') return null
  return this.call('homeAiSend', prompt)
}

// An item's press: a deck opens as the deck (the boards row's own opener,
// functions/assistantBoard.js assistantBoardSelect — a saved deck's record is
// a DeckSpec).
export const homeTabStartOpen = function homeTabStartOpen(s) {
  const item = s && typeof s.parse === 'function' ? s.parse() : s
  if (!item || typeof item !== 'object') return null
  if (item.kind === 'deck' && item.id) return this.call('assistantBoardSelect', item.id)
  return null
}
